// Correo del paso 01: banda verde si ya está verificado; si falta, captura, código y reenvío.
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError } from '@/api/errors';
import { Button, Input, Select } from '@/kit';
import { cn } from '@/lib/cn';
import { useSendEmailOtp, useVerifyEmailOtp, WIZARD_KEYS } from '../api/useWizard';
import {
    EMAIL_ALREADY_EXISTS_MESSAGE,
    EMAIL_NOT_VERIFIED_MESSAGE,
    isEmailAlreadyExists,
    sendEmailOtpMessage,
    verifyEmailOtpMessage,
} from '../emailOtpMessages';
import {
    formatCountdown,
    OTP_LIFETIME_TEXT,
    OTP_RESEND_SECONDS,
    resendWaitSeconds,
} from '../otpResend';
import { EMAIL_KIND_OPTIONS, emailKindLabel, isEmailFormatValid } from '../sessionEmail';
import type { EmailKind, SessionEmail, TakenEmail, WizardSession } from '../types';
import { emptyOtpCode, OtpCodeBoxes } from './OtpCodeBoxes';

function SectionLabel({ children }: { children: string }) {
    return (
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {children}
        </span>
    );
}

/**
 * Sección de correo del paso 01. `highlight` la marca cuando el silo rebotó «Continuar»;
 * `taken` trae el correo rechazado por ser de otro cliente: regresa a la captura prellenada con él.
 */
export function EmailVerifyBlock({
    sessionId,
    session,
    highlight = false,
    taken,
    onEmailVerified,
}: {
    sessionId: string;
    session: WizardSession;
    highlight?: boolean;
    taken?: TakenEmail;
    onEmailVerified?: () => void;
}) {
    const email = session.email;
    // Venta que aceptó términos antes del correo: no se pide.
    if (!email && session.consentsAccepted) return null;
    return (
        <div className="mt-6 flex flex-col gap-3">
            <div className="h-px bg-border" />
            {email && email.state !== 'PENDING' && !taken ? (
                <EmailVerifiedBand email={email} />
            ) : (
                <EmailCapture
                    sessionId={sessionId}
                    masked={taken ? undefined : email?.masked}
                    initialAddress={taken?.address}
                    initialKind={taken?.kind}
                    highlight={highlight}
                    taken={taken !== undefined}
                    onEmailVerified={onEmailVerified}
                />
            )}
        </div>
    );
}

function EmailVerifiedBand({ email }: { email: SessionEmail }) {
    const shown = email.address ?? email.masked ?? '';
    const kind = emailKindLabel(email.kind);
    return (
        <div className="flex flex-col gap-2">
            <SectionLabel>Correo electrónico</SectionLabel>
            <div className="rounded-md border border-success-soft-border bg-success-soft px-3 py-2 text-[12px] font-medium text-success">
                {`Correo ${shown} verificado${kind ? ` · ${kind}` : ''}`}
            </div>
            {email.state === 'EXISTING' && (
                <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                    El cliente ya tiene este correo registrado. No hace falta pedirlo; se guarda en
                    su expediente.
                </p>
            )}
        </div>
    );
}

function EmailCapture({
    sessionId,
    masked,
    initialAddress,
    initialKind,
    highlight,
    taken,
    onEmailVerified,
}: {
    sessionId: string;
    masked?: string;
    initialAddress?: string;
    initialKind?: EmailKind;
    highlight: boolean;
    taken: boolean;
    onEmailVerified?: () => void;
}) {
    const sendEmailOtp = useSendEmailOtp(sessionId);
    const verifyEmailOtp = useVerifyEmailOtp(sessionId);
    const qc = useQueryClient();
    const [kind, setKind] = useState<EmailKind>(initialKind ?? 'PERSONAL');
    const [address, setAddress] = useState(initialAddress ?? '');
    // Con un código ya enviado (recarga), se sigue en las cajas con el correo enmascarado.
    const [sentTo, setSentTo] = useState<string | undefined>(masked);
    const [code, setCode] = useState<string[]>(emptyOtpCode);
    const [resendIn, setResendIn] = useState(0);
    // El aviso de correo de otro cliente se queda junto al campo hasta que lo editen.
    const [takenNotice, setTakenNotice] = useState(taken);
    const inputs = useRef<(HTMLInputElement | null)[]>([]);
    // Evita un segundo envío por doble clic antes de que el botón se deshabilite.
    const sending = useRef(false);

    useEffect(() => {
        if (resendIn <= 0) return;
        const t = setTimeout(() => {
            setResendIn((n) => n - 1);
        }, 1000);
        return () => {
            clearTimeout(t);
        };
    }, [resendIn]);

    const canSend = isEmailFormatValid(address) && !takenNotice;

    /** Manda el código al correo tecleado; el silo lo normaliza y cuida la espera entre envíos. */
    const send = async (): Promise<void> => {
        if (sending.current || !canSend) return;
        sending.current = true;
        const target = address.trim();
        try {
            await sendEmailOtp.mutateAsync({ email: target, kind });
            setSentTo(target);
            setCode(emptyOtpCode());
            setResendIn(OTP_RESEND_SECONDS);
            toast.success(`Te mandamos el código a ${target}.`);
        } catch (error) {
            // Correo de otro cliente: regresa a la captura con el aviso en el campo, sin toast.
            if (isEmailAlreadyExists(error)) {
                setSentTo(undefined);
                setTakenNotice(true);
                return;
            }
            const seconds = resendWaitSeconds(error);
            if (seconds !== undefined) setResendIn(seconds);
            toast.error(sendEmailOtpMessage(error));
        } finally {
            sending.current = false;
        }
    };

    const submitCode = async (full: string): Promise<void> => {
        try {
            await verifyEmailOtp.mutateAsync(full);
            toast.success('Correo verificado');
            onEmailVerified?.();
        } catch (error) {
            toast.error(verifyEmailOtpMessage(error));
            // Ya usado: si el correo quedó verificado en otra pestaña, la sesión releída lo pinta.
            if (error instanceof ApiError && error.code === 'OTP_ALREADY_USED') {
                void qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) });
            }
            setCode(emptyOtpCode());
            inputs.current[0]?.focus();
        }
    };

    const changeEmail = (): void => {
        setSentTo(undefined);
        setCode(emptyOtpCode());
    };

    return (
        <div
            className={cn(
                'flex flex-col gap-3',
                highlight && 'rounded-md border border-destructive/30 bg-destructive/5 p-3',
            )}
        >
            <SectionLabel>Correo electrónico</SectionLabel>
            {highlight && (
                <p className="text-[11.5px] leading-relaxed text-destructive">
                    {EMAIL_NOT_VERIFIED_MESSAGE}
                </p>
            )}

            {sentTo === undefined ? (
                <div className="flex flex-col gap-1.5">
                    <div className="flex flex-wrap items-stretch gap-2">
                        <Select
                            id="email-kind"
                            value={kind}
                            onChange={(value) => {
                                setKind(value as EmailKind);
                            }}
                            options={EMAIL_KIND_OPTIONS}
                            className="w-[150px]"
                        />
                        <Input
                            type="email"
                            autoComplete="email"
                            placeholder="nombre@correo.com"
                            aria-label="Correo del cliente"
                            aria-invalid={takenNotice || undefined}
                            aria-describedby={takenNotice ? 'email-taken' : undefined}
                            value={address}
                            onChange={(e) => {
                                setAddress(e.target.value);
                                setTakenNotice(false);
                            }}
                            className={cn(
                                'min-w-[200px] flex-1',
                                takenNotice &&
                                    'border-destructive focus-visible:border-destructive',
                            )}
                        />
                        <Button
                            disabled={!canSend}
                            isLoading={sendEmailOtp.isPending}
                            onClick={() => void send()}
                        >
                            Enviar código
                        </Button>
                    </div>
                    {takenNotice && (
                        <p id="email-taken" role="alert" className="text-[11.5px] text-destructive">
                            {EMAIL_ALREADY_EXISTS_MESSAGE}
                        </p>
                    )}
                </div>
            ) : (
                <>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <SectionLabel>Código del correo</SectionLabel>
                        <span className="text-[11.5px] text-muted-foreground">
                            Te mandamos el código a{' '}
                            <span className="font-medium text-foreground">{sentTo}</span>.{' '}
                            {OTP_LIFETIME_TEXT}
                            {' · '}
                            <button
                                type="button"
                                onClick={changeEmail}
                                className="font-medium text-primary underline decoration-dotted underline-offset-2 transition-colors hover:text-primary/80"
                            >
                                Cambiar correo
                            </button>
                        </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                        <OtpCodeBoxes
                            code={code}
                            onCodeChange={setCode}
                            onComplete={(full) => void submitCode(full)}
                            inputsRef={inputs}
                            labelPrefix="Código del correo, dígito"
                        />
                        <Button
                            variant="ghost"
                            size="sm"
                            className="ml-0 sm:ml-2"
                            disabled={resendIn > 0 || !canSend}
                            isLoading={sendEmailOtp.isPending}
                            onClick={() => void send()}
                        >
                            {resendIn > 0 ? `Reenviar en ${formatCountdown(resendIn)}` : 'Reenviar'}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}
